import { hFetch } from '@/lib/hfetch';
import type { ContactEntity } from '../entity_types/Contact';
import type { ContactEventEntity, ContactEventType } from '../entity_types/ContactEvent';

type GetTeamResponse = {
  company: string;
  event_name: string;
  event_description: string;
  company_logo: string | null;
  contact: ContactEntity | null;
  existing_event: ContactEventEntity | null;
  event_address: string | null;
};

type GetTeamArgs = {
  id: string;
  event_type: ContactEventType;
  contact_id?: string;
  humaniz_checkin_user_id?: string;
};

export const getTeam = async (args: GetTeamArgs) => {
  const params = new URLSearchParams();
  params.append('id', args.id);
  params.append('event_type', args.event_type);
  if (args.contact_id) params.append('contact_id', args.contact_id);
  if (args.humaniz_checkin_user_id) params.append('humaniz_checkin_user_id', args.humaniz_checkin_user_id);

  return await hFetch<GetTeamResponse>({ url: `/public/contact_interview/team?${params.toString()}` });
};

type SlotData = {
  start_time: string;
  end_time: string;
};

type GetEventSlotsArgs = {
  id: string;
  event_type: ContactEventType;
  contact_id?: string;
  humaniz_checkin_user_id?: string;
};

export const getEventSlots = async (args: GetEventSlotsArgs) => {
  const params = new URLSearchParams();
  params.append('event_type', args.event_type);
  if (args.contact_id) params.append('contact_id', args.contact_id);
  if (args.humaniz_checkin_user_id) params.append('humaniz_checkin_user_id', args.humaniz_checkin_user_id);

  return await hFetch<SlotData[]>({ url: `/public/contact_interview/${args.id}?${params.toString()}` });
};

type SharedArgs = {
  company_slug: string;
  start_time: string;
  end_time: string;
  contact: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  event_type: ContactEventType;
  contact_id: string;
};

type PublicArgs = SharedArgs & {
  scheduled_by: 'public';
  humaniz_checkin_user_id?: string;
};

type AdminArgs = SharedArgs & {
  scheduled_by: 'admin';
  location: string;
  address?: string;
  interviewer_id?: number;
};

type CreateContactInterviewArgs = PublicArgs | AdminArgs;

export const createContactInterview = async (args: CreateContactInterviewArgs) => {
  const { event_type, contact_id, company_slug, start_time, end_time, contact, scheduled_by } = args;

  const params = new URLSearchParams();
  params.append('event_type', event_type);
  params.append('contact_id', contact_id);
  params.append('scheduled_by', scheduled_by);

  if (args.scheduled_by === 'public' && args.humaniz_checkin_user_id) {
    params.append('humaniz_checkin_user_id', args.humaniz_checkin_user_id);
  }

  return await hFetch({
    url: `/public/contact_interview?${params.toString()}`,
    method: 'POST',
    payload: {
      id: company_slug,
      start_time,
      end_time,
      first_name: contact.firstName,
      last_name: contact.lastName,
      email: contact.email,
      phone_number: contact.phone,
      ...(args.scheduled_by === 'admin' && {
        location: args.location,
        address: args.address,
        interviewer_id: args.interviewer_id,
      }),
    },
  });
};
